import {PanoramaTransactions} from '../../../../constants/app';
import {mapUserInputSelectionMapToChoices} from '../../mappers/mapUserInputSelectionMapToChoices';
import {RootStore} from '../../stores/RootStore';
import {getAppErrorHandler} from './getAppErrorHandler';
import {getCtaButtonClickHandler} from './getCtaButtonClickHandler';
import {WidgetEventHandler} from './types';
import {AfterAddToCartChoices, CtaButtonsWidgetComponentIds} from '../../../ctaButtonsWidget/config/constants';
import {mapUserInputTextToFields} from '../../mappers/mapUserInputTextToFields';
import {action} from 'mobx';
import {SPECS} from '../../../../specs';
import {createCheckout} from '../createCheckout';
import {interruptAction} from './interruptAction';
import {getActualSubscriptionPlanId} from '../product-service/subscriptions/getActualSubscriptionPlanId';

const getSelectedAction = (rootStore: RootStore, isPreOrder: boolean) => {
  const flowAPI = rootStore.controllerParams.flowAPI;
  const isMobile = flowAPI.environment.isMobile;
  const settings = rootStore.services.components.ctaButtonsWidget.settings;
  const afterAddToCartDesktop = settings?.afterAddToCart;
  const afterAddToCartMobile = settings?.afterAddToCartMobile ?? afterAddToCartDesktop;
  const addToCartAction = isMobile ? afterAddToCartMobile : afterAddToCartDesktop;
  const afterCompletionAction = isPreOrder ? settings?.afterPreOrder : addToCartAction;
  return afterCompletionAction || AfterAddToCartChoices.MINI_CART;
};

function preloadCartByAction(ecomPlatformPublicAPI, addToCartAction: AfterAddToCartChoices) {
  if (addToCartAction === AfterAddToCartChoices.TINY_CART) {
    return ecomPlatformPublicAPI.cart.preloadTinyCart();
  } else if (addToCartAction === AfterAddToCartChoices.MINI_CART) {
    return ecomPlatformPublicAPI.cart.preloadSideCart();
  } else if (addToCartAction === AfterAddToCartChoices.CART) {
    return ecomPlatformPublicAPI.cart.preloadCartPage();
  }
}

const getButtonIdForIndicator = (rootStore: RootStore) => {
  const flowAPI = rootStore.controllerParams.flowAPI;
  const {isPreOrder, isSubscribeNow} = rootStore.$state;
  const isMS3 = flowAPI.experiments.enabled(SPECS.PRODUCT_PAGE_BLOCKS_MS3);
  if (isMS3 && isPreOrder) {
    return CtaButtonsWidgetComponentIds.PreOrderButton;
  }
  if (rootStore.isExperimentEnabled(SPECS.AddToCartWithSubscription) && isMS3 && isSubscribeNow) {
    return CtaButtonsWidgetComponentIds.SubscribeWithCartBtn;
  }
  return CtaButtonsWidgetComponentIds.AddToCartBtn;
};

const onAddToCartCompleted = (rootStore: RootStore) => async (): Promise<void> => {
  const flowAPI = rootStore.controllerParams.flowAPI;
  const isMobile = flowAPI.environment.isMobile;
  const {isPreOrder} = rootStore.$state;
  const selectedAction = getSelectedAction(rootStore, isPreOrder);

  if (selectedAction === AfterAddToCartChoices.CHECKOUT) {
    return createCheckout(rootStore, {withNavigateToCheckout: true, isPreOrder}).then(() => Promise.resolve());
  }

  switch (selectedAction) {
    case AfterAddToCartChoices.CART:
      return rootStore.ecomPublicApi.navigate.toCart();

    case AfterAddToCartChoices.MINI_CART:
      try {
        return Promise.resolve(await rootStore.ecomPublicApi.cart.openSideCart());
      } catch {
        return rootStore.ecomPublicApi.navigate.toCart();
      }
    case AfterAddToCartChoices.TINY_CART:
      if (isMobile) {
        return rootStore.ecomPublicApi.navigate.toCart();
      }
      return Promise.resolve(rootStore.ecomPublicApi.cart.showTinycart());
    case AfterAddToCartChoices.NONE:
      rootStore.$state.isAddToCartInProgress = false;
      return rootStore.services.components.ctaButtonsWidget.indicateAddToCartCompleted(
        getButtonIdForIndicator(rootStore),
      );
    default:
      return Promise.resolve();
  }
};

const handleAddToCart = async (rootStore: RootStore): Promise<void> => {
  rootStore.$state.isAddToCartInProgress = true;
  const productId = rootStore.$state.product.id;
  const quantity = rootStore.$state.quantityInput;
  const preOrderRequested = rootStore.$state.isPreOrder;
  const customTextFields = mapUserInputTextToFields(rootStore);
  const choices = mapUserInputSelectionMapToChoices(rootStore);
  const subscriptionOptionId = getActualSubscriptionPlanId(rootStore.$state);
  const addProductsParams = [
    {
      productId,
      quantity,
      preOrderRequested,
      options: {
        customTextFields,
        choices,
        ...(rootStore.isExperimentEnabled(SPECS.AddToCartWithSubscription) ? {subscriptionOptionId} : {}),
      },
      ...(rootStore.isExperimentEnabled(SPECS.AddToCartWithTrackData) ? {trackData: rootStore.$state.trackParams} : {}),
    },
  ];
  const errorHandler = getAppErrorHandler(rootStore);
  const onAddToCartCompletedHandler = onAddToCartCompleted(rootStore);
  const publicApi = rootStore.storesPublicApi;

  if (rootStore.isExperimentEnabled(SPECS.PreloadCarts)) {
    const selectedAction = getSelectedAction(rootStore, preOrderRequested);

    return Promise.all([
      publicApi!.cart.addProducts(addProductsParams),
      preloadCartByAction(rootStore.ecomPublicApi, selectedAction),
    ])
      .then(() => onAddToCartCompletedHandler())
      .catch(errorHandler)
      .finally(action(() => (rootStore.$state.isAddToCartInProgress = false)));
  }

  return publicApi!.cart
    .addProducts(addProductsParams)
    .then(onAddToCartCompletedHandler)
    .catch(errorHandler)
    .finally(action(() => (rootStore.$state.isAddToCartInProgress = false)));
};

export const handleAddToCartClick = (rootStore: RootStore): WidgetEventHandler<any> => {
  return getCtaButtonClickHandler(rootStore, async () => {
    const addToCartAction = () =>
      rootStore.reportPanoramaTransaction(PanoramaTransactions.AddToCart, () => handleAddToCart(rootStore));

    return interruptAction(rootStore.wixCodeService.onAddToCartInterruption, addToCartAction);
  });
};
